import { DEBOUNCE_MS } from '@/constants/timing';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ClientsGridFilterModel } from '@/modules/clientServices/utils/clientsGridState';

interface UseServerBackedQuickFilterOptions {
  debounceMs?: number;
  /**
   * A search term to restore on mount (e.g. from the URL/sessionStorage), so
   * the very first render already reflects it — both in the grid's quick
   * filter and in `serverSearchTerm` — instead of waiting out the debounce.
   * Without it the seeded state is identical to today's behaviour. Mutually
   * exclusive with `initialFilterModel` — pass whichever the caller's grid
   * needs restored.
   */
  initialValue?: string;
  initialFilterModel?: ClientsGridFilterModel;
}

interface NormalizedClientsGridFilterModel extends ClientsGridFilterModel {
  quickFilterValues: string[];
}

const createEmptyFilterModel = (): NormalizedClientsGridFilterModel => ({
  items: [],
  quickFilterValues: [],
});

const hasQuickFilterValues = (
  filterModel: ClientsGridFilterModel,
): filterModel is NormalizedClientsGridFilterModel =>
  filterModel.quickFilterValues !== undefined;

const normalizeFilterModel = (
  filterModel: ClientsGridFilterModel,
): NormalizedClientsGridFilterModel =>
  hasQuickFilterValues(filterModel)
    ? filterModel
    : {
        ...filterModel,
        quickFilterValues: [],
      };

const getQuickFilterText = (
  filterModel: NormalizedClientsGridFilterModel,
): string => filterModel.quickFilterValues.join(' ').trim();

export function useServerBackedQuickFilter(
  options?: UseServerBackedQuickFilterOptions,
) {
  const {
    debounceMs = DEBOUNCE_MS.DEFAULT,
    initialValue,
    initialFilterModel,
  } = options || {};
  const initialModel = useRef(
    initialFilterModel
      ? normalizeFilterModel(initialFilterModel)
      : initialValue
        ? { items: [], quickFilterValues: [initialValue] }
        : createEmptyFilterModel(),
  );

  const [filterModel, setFilterModel] =
    useState<NormalizedClientsGridFilterModel>(initialModel.current);
  const [serverSearchTerm, setServerSearchTerm] = useState<string>(() =>
    getQuickFilterText(initialModel.current),
  );
  const debounceRef = useRef<number | undefined>(undefined);

  const quickFilterText = useMemo(
    () => getQuickFilterText(filterModel),
    [filterModel],
  );

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setServerSearchTerm(quickFilterText);
    }, debounceMs);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [quickFilterText, debounceMs]);

  return {
    filterModel,
    setFilterModel,
    onFilterModelChange: (model: ClientsGridFilterModel) => {
      setFilterModel(normalizeFilterModel(model));
    },
    serverSearchTerm,
  };
}
